import React from 'react';
import ChevronLeftSvg from 'pubweb-smokey/dist/images/svg/iconography-16x16/chevron-lft.svg';
import ChevronRightSvg from 'pubweb-smokey/dist/images/svg/iconography-16x16/chevron-right.svg';
import { cn } from '@utils/cn';

type Props = {
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  isDisabled: boolean;
  onClickNewPage: (newPage: number) => void;
};

export default function Paginator({
  currentPage,
  totalItems,
  itemsPerPage,
  isDisabled,
  onClickNewPage,
}: Props): JSX.Element {
  const lastPage = Math.ceil(totalItems / itemsPerPage);

  let leftPageNumber = currentPage - 1;
  let middlePageNumber = currentPage;
  let rightPageNumber = currentPage + 1;

  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === lastPage;

  // Current page should always be shown in the middle page number (unless we are on the first or last page).
  // If we are on the first page, current page should be the left page number.
  // If we are on the last page, current page should be the right page number (unless there are less than 3 pages total).
  if (isFirstPage) {
    leftPageNumber = 1;
    middlePageNumber = 2;
    rightPageNumber = 3;
  } else if (isLastPage && lastPage >= 3) {
    leftPageNumber = currentPage - 2;
    middlePageNumber = currentPage - 1;
    rightPageNumber = currentPage;
  }

  // Example:
  //    currentPage is 2
  //    itemsPerPage is 21
  //
  //    21 * (2-1) = 21, the previous page's last item is 21
  //    21 + 1 = 22, the first item on page 2 is 22
  const lastItemOnPreviousPage = itemsPerPage * (currentPage - 1);
  const firstItemOnCurrentPage = lastItemOnPreviousPage + 1;

  // Example:
  //    currentPage is 2
  //    itemsPerPage is 21
  //    firstItemOnCurrentPage is 22
  //
  //    2 * 21 = 42, the last item on page 2 is 42
  //
  //    The range of page 1 is 1 - 21
  //    The range of page 2 is 22 - 42
  //    The range of page 3 is 43 - 63
  const lastItemOnCurrentPage = isLastPage
    ? totalItems
    : currentPage * itemsPerPage;

  return (
    <div className="mx-auto mb-12 w-fit">
      <div className="flex items-center">
        <button
          aria-label="previous-page-button"
          aria-hidden={isFirstPage}
          className={cn('grid place-content-center p-3', {
            invisible: isFirstPage,
          })}
          disabled={isDisabled}
          onClick={() => onClickNewPage(currentPage - 1)}
        >
          <div
            className={cn(
              'grid size-12 place-content-center bg-primary-claytonBlue-standard',
              {
                'bg-accent-grey4-standard': isDisabled,
              }
            )}
          >
            <div className="ml-[0.625rem] mt-3">
              <ChevronLeftSvg style={{ fill: 'white' }} />
            </div>
          </div>
        </button>

        <div className="flex">
          <div
            aria-label={
              currentPage === leftPageNumber
                ? 'Current Page Number'
                : 'Left Page Number'
            }
            className={cn('grid size-9 place-content-center', {
              '-gray-200 border': currentPage === leftPageNumber,
            })}
          >
            {leftPageNumber}
          </div>
          <div
            aria-label={
              currentPage === middlePageNumber
                ? 'Current Page Number'
                : 'Middle Page Number'
            }
            aria-hidden={middlePageNumber > lastPage}
            className={cn('grid size-9 place-content-center', {
              invisible: middlePageNumber > lastPage,
              '-gray-200 border': currentPage === middlePageNumber,
            })}
          >
            {middlePageNumber}
          </div>
          <div
            aria-label={
              currentPage === rightPageNumber
                ? 'Current Page Number'
                : 'Right Page Number'
            }
            aria-hidden={rightPageNumber > lastPage}
            className={cn('grid size-9 place-content-center', {
              invisible: rightPageNumber > lastPage,
              '-gray-200 border': currentPage === rightPageNumber,
            })}
          >
            {rightPageNumber}
          </div>
        </div>

        <button
          aria-label="next-page-button"
          aria-hidden={isLastPage}
          className={cn('grid place-content-center p-3', {
            invisible: isLastPage,
          })}
          disabled={isDisabled}
          onClick={() => onClickNewPage(currentPage + 1)}
        >
          <div
            className={cn(
              'grid size-12 place-content-center bg-primary-claytonBlue-standard',
              {
                'bg-accent-grey4-standard': isDisabled,
              }
            )}
          >
            <div className="ml-[0.625rem] mt-3">
              <ChevronRightSvg style={{ fill: 'white' }} />
            </div>
          </div>
        </button>
      </div>

      <div className="mt-3.5 text-center text-xs" aria-label="Showing Text">
        Showing {firstItemOnCurrentPage}-{lastItemOnCurrentPage} of {totalItems}
      </div>
    </div>
  );
}
